import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  Input,
  OnInit,
  OnDestroy,
  ViewChild,
  computed,
  model,
  signal,
  inject,
  OnChanges,
  SimpleChanges,
} from '@angular/core';
import { Router } from '@angular/router';
import { Subject } from 'rxjs';
import { FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { DashboardService } from '../../dashboard/dashboard.service';
import { SessionStorageService } from '../../services/session-storage.service';
import { debounceTime, distinctUntilChanged, takeUntil } from 'rxjs/operators';
import { DateTime } from 'luxon';
import { COMMA, ENTER } from '@angular/cdk/keycodes';
import {
  MatAutocompleteSelectedEvent,
  MatAutocompleteModule,
} from '@angular/material/autocomplete';
import { DtccService } from '../../annuity-lookup/dtcc.service';
import { UniversalSearchService } from '../../universal-search/services/universal-search.service';
import { BookManagementService } from '../book-management.service';

import { SimpleChipFilterComponent } from './simple-chip-filter/simple-chip-filter.component';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatChipsModule } from '@angular/material/chips';
import { CommonModule } from '@angular/common';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatButtonModule } from '@angular/material/button';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { NgxLoadingModule } from 'ngx-loading';
import { AdminService } from '../../admin/admin.service';

@Component({
  selector: 'app-bm-widget-filters',
  templateUrl: './bm-widget-filters.component.html',
  styleUrls: ['./bm-widget-filters.component.scss'],
  imports: [
    SimpleChipFilterComponent,
    MatIconModule,
    MatMenuModule,
    MatFormFieldModule,
    MatAutocompleteModule,
    ReactiveFormsModule,
    MatProgressSpinnerModule,
    MatChipsModule,
    FormsModule,
    CommonModule,
    MatDatepickerModule,
    MatTooltipModule,
    MatButtonModule,
    MatInputModule,
    MatSelectModule,
    NgxLoadingModule,
  ],
  providers: [],
})
export class BmWidgetFiltersComponent
  implements AfterViewInit, OnInit, OnDestroy, OnChanges
{
  private bmSvc = inject(BookManagementService);
  private changeDetector = inject(ChangeDetectorRef);
  private adminService = inject(AdminService);
  private dtccSvc = inject(DtccService);
  private route = inject(Router);
  private ss = inject(SessionStorageService);
  private usServ = inject(UniversalSearchService);

  @ViewChild('pickerField') pickerField;
  @Input() viewMode;
  @Input() minDate;
  @Input() defaultRange;
  @Input() preexistingWidgetFilters;

  filteredUnits;
  unitCtrl = new FormControl();
  selectedUnit = '';
  npn = new FormControl();
  selectedNpn = '';
  dateRange = {
    start: '',
    end: '',
  };
  unsubscribe: Subject<any> = new Subject();
  private destroyed = false;
  floatingRangeOptions = [
    { value: '7 days', display: 'Last 7 Days' },
    { value: '30 days', display: 'Last 30 Days' },
    { value: '90 days', display: 'Last 90 Days' },
    { value: 'mtd', display: 'Month-to-Date' },
    { value: 'ytd', display: 'Year-to-Date' },
    { value: '12mtd', display: '12 Months (to Date)' },
    { value: 'custom', display: 'Custom Range' },
  ];
  presetRangeControl = new FormControl<any>('');

  dropdownLoading = false;
  dropdownNoResults = false;
  dropdownLoadingStandalone = false;
  dropdownNoResultsStandalone = false;
  userControl = new FormControl();
  userControlStandalone = new FormControl();
  filteredUsers = [];
  filteredUsersStandalone = [];
  fetchingNpns = false;
  chips = signal<string[]>([]);
  separatorKeysCodes: number[] = [ENTER, COMMA];
  currentChip = model('');
  chipOptions = signal([]);
  filteredChips = computed(() => {
    const currentChip = this.currentChip().toLowerCase();
    return currentChip
      ? this.chipOptions().filter(
          chip =>
            chip.Name.toLowerCase().includes(currentChip) ||
            chip.NPN.toLowerCase().includes(currentChip)
        )
      : this.chipOptions().slice();
  });
  moreFilters = [
    {
      label: 'Account Type',
      fieldVar: 'accountType',
      value: signal<string[]>([]),
    },
    {
      label: 'Annuity Type',
      fieldVar: 'productType',
      value: signal<string[]>([]),
    },
    { label: 'Carrier', fieldVar: 'carrier', value: signal<string[]>([]) },
    { label: 'Product', fieldVar: 'productName', value: signal<string[]>([]) },
  ];
  filtersUpdated = false;
  homeOfficeUser: boolean = false;
  selectedStandaloneUser = null;
  // True while any widget is loading; drives the filter backdrop and disabling.
  searching = signal(false);
  hasRepCodes: boolean = false;
  isProducer: boolean = false;

  ngOnInit(): void {
    const globals = this.ss.get('globals');
    const rights = this.ss.get('rights');
    const repCodes = this.ss.get('repUnits');
    if (globals?.user?.managerUnitCount > 0) {
      this.homeOfficeUser = true;
    } else if (globals?.user?.managerUnitCount === 0) {
      this.homeOfficeUser = false;
    }

    if (repCodes && repCodes.length > 0) {
      this.hasRepCodes = true;
      this.isProducer = rights?.includes('BookManagement');
    }

    this.bmSvc.widgetsLoading$
      .pipe(takeUntil(this.unsubscribe))
      .subscribe(loading => this.searching.set(loading));
    this.filteredUnits = globals?.user.managerUnits;
    if (this.filteredUnits && this.filteredUnits.length > 0) {
      this.getUnits();
    }
    this.userControl.valueChanges
      .pipe(
        debounceTime(500),
        distinctUntilChanged(),
        takeUntil(this.unsubscribe)
      )
      .subscribe(value => {
        this.dropdownLoading = true;
        if (value && typeof value === 'string') {
          return this._filterReps(value);
        } else if (!value) {
          this.filteredUsers = [];
          this.dropdownLoading = false;
        } else {
          this.dropdownLoading = false;
        }
      });
    this.userControlStandalone.valueChanges
      .pipe(
        debounceTime(500),
        distinctUntilChanged(),
        takeUntil(this.unsubscribe)
      )
      .subscribe(value => {
        this.dropdownLoadingStandalone = true;
        if (value && typeof value === 'string') {
          return this._filterRepsStandalone(value);
        } else if (!value) {
          this.filteredUsersStandalone = [];
          this.dropdownLoadingStandalone = false;
        } else {
          this.dropdownLoadingStandalone = false;
        }
      });

    // this.bmSvc.triggerViewSwitch$
    //   .pipe(takeUntil(this.unsubscribe))
    //   .subscribe(val => {
    //     if (Object.prototype.hasOwnProperty.call(val, 'filterVals')) {
    //       this.bmSvc.updateBmWidgetFilters(val['filterVals']);
    //     }
    //   });

    if (this.viewMode === 'rep') {
      this.fetchingNpns = true;
      this.dtccSvc.getNPNs(globals.user.id).subscribe(resp => {
        this.fetchingNpns = false;
        this.chipOptions.set(resp.npns);
      });
    }

    if (this.defaultRange) {
      this.presetRangeControl.setValue(this.defaultRange);

      this.calculateDateRange(this.defaultRange);
    }
  }

  ngAfterViewInit(): void {
    if (
      !(
        this.viewMode === 'rep' &&
        this.homeOfficeUser &&
        !this.ss.get('BookManagementWidget')
      ) ||
      this.isProducer
    ) {
      // Defer the initial search until the current change detection pass
      // finishes; emitting synchronously flips sibling widgets' loading state
      // after they were checked (NG0100 ExpressionChangedAfterItHasBeenChecked).
      void Promise.resolve().then(() => {
        if (!this.destroyed) {
          this.triggerSearch();
        }
      });
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes?.preexistingWidgetFilters?.currentValue?.['search']) {
      this.hydrateFilters(
        changes?.preexistingWidgetFilters?.currentValue['search']
      );
      this.triggerSearch(true);
    }
  }

  ngOnDestroy() {
    this.destroyed = true;
    this.unsubscribe.next(null);
    this.unsubscribe.complete();
  }

  getUnits() {
    this.unitCtrl.setValue(this.filteredUnits[0].id);
    this.selectedUnit = this.filteredUnits[0].id;
    this.unitCtrl.valueChanges
      .pipe(distinctUntilChanged(), takeUntil(this.unsubscribe))
      .subscribe(unit => {
        this._filterUnits(unit);
      });
    this.npn.valueChanges
      .pipe(
        debounceTime(400),
        distinctUntilChanged(),
        takeUntil(this.unsubscribe)
      )
      .subscribe(npnInput => {
        this.selectedNpn = npnInput;
      });
  }

  private _filterUnits(value: string) {
    this.adminService.getUnitList(value, true).subscribe(resp => {
      if (resp.names) {
        this.filteredUnits = resp.names;
      } else {
        this.filteredUnits = [];
      }
    });
  }

  private _filterReps(value: string) {
    this.dropdownLoading = true;
    this.usServ.userSearch(value).subscribe(res => {
      this.dropdownLoading = false;
      if (res && res.names.length > 0) {
        this.dropdownNoResults = false;
        this.filteredUsers = res.names;
      } else if (!res || res.names.length === 0) {
        this.dropdownNoResults = true;
        this.changeDetector.detectChanges();
      }
    });
  }

  private _filterRepsStandalone(value: string) {
    this.dropdownLoadingStandalone = true;
    this.usServ.userSearch(value).subscribe(res => {
      this.dropdownLoadingStandalone = false;
      if (res && res.names.length > 0) {
        this.dropdownNoResultsStandalone = false;
        this.filteredUsersStandalone = res.names;
      } else if (!res || res.names.length === 0) {
        this.dropdownNoResultsStandalone = true;
        this.changeDetector.detectChanges();
      }
    });
  }

  nav(location) {
    this.route.navigate([`/${location}`]);
  }

  selectUnit(ev) {
    this.unitCtrl.setValue(ev.option.value);
    this.selectedUnit = ev.option.value;
    this.filtersUpdated = true;
  }

  rangeSelection(ev) {
    const { value } = ev;
    const endDateToday = DateTime.now().toUTC().toISO();
    if (value === 'custom') {
      setTimeout(() => {
        this.pickerField.open();
      });
    } else if (value === 'mtd') {
      // Set the date picker with the selected times
      const calculatedStartDate = DateTime.now()
        .startOf('month')
        .toUTC()
        .toISO();
      this.dateRange.start = calculatedStartDate;
      this.dateRange.end = endDateToday;
    } else if (value === 'ytd') {
      // Set the date picker with the selected times
      const calculatedStartDate = DateTime.now()
        .startOf('year')
        .toUTC()
        .toISO();
      this.dateRange.start = calculatedStartDate;
      this.dateRange.end = endDateToday;
    } else if (value === '12mtd') {
      // Set the date picker with the selected times
      const calculatedStartDate = DateTime.now()
        .minus({ years: 1 })
        .startOf('month')
        .toUTC()
        .toISO();
      this.dateRange.start = calculatedStartDate;
      this.dateRange.end = endDateToday;
    } else {
      this.calculateDateRange(value);
    }
    this.filtersUpdated = true;
  }

  dateChange(ev, position) {
    this.presetRangeControl.setValue('custom');
    if (position === 'start') {
      if (
        this.minDate &&
        DateTime.fromJSDate(new Date(ev.value)).startOf('day').toUTC().toISO() <
          this.minDate
      ) {
        this.dateRange[position] = this.minDate;
      } else {
        this.dateRange[position] = DateTime.fromJSDate(new Date(ev.value))
          .startOf('day')
          .toUTC()
          .toISO()
          .toLocaleString();
      }
    } else {
      if (ev.value) {
        this.dateRange[position] = DateTime.fromJSDate(new Date(ev.value))
          .endOf('day')
          .toUTC()
          .toISO();
      } else {
        this.dateRange[position] = DateTime.now().endOf('day').toUTC().toISO();
      }
    }
    this.filtersUpdated = true;
  }

  clearDateField(ev?) {
    if (ev) {
      ev.preventDefault();
      ev.stopPropagation();
    }
    this.dateRange = {
      start: '',
      end: '',
    };
    this.presetRangeControl.setValue('');

    if (this.viewMode === 'carrier') {
      this.filtersUpdated = false;
    }
    this.filtersUpdated = true;
  }

  calculateDateRange(value) {
    // Calculate time difference
    const calculateTime = value.split(' ')[0];
    const calculateUnit = value.split(' ')[1];
    const timeDiffObject = {};
    timeDiffObject[calculateUnit] = calculateTime;
    const calculatedStartDate = DateTime.now()
      .minus(timeDiffObject)
      .startOf('day')
      .toUTC()
      .toISO();
    const endDateToday = DateTime.now().endOf('day').toUTC().toISO();
    // Set the date picker with the selected times
    this.dateRange.start = calculatedStartDate;
    this.dateRange.end = endDateToday;
  }

  clearRepSearch() {
    this.userControl.setValue('');
    this.chips.update(() => []);
    this.currentChip.set('');
  }

  clearUserStandalone() {
    this.userControlStandalone.setValue('');
    this.selectedStandaloneUser = null;
    this.filtersUpdated = true;
  }

  clearNpnSearch() {
    this.currentChip.set('');
  }

  removeNpn(npn: string): void {
    this.chips.update((chips: string[]) => {
      const index = chips.indexOf(npn);
      if (index < 0) {
        return chips;
      }

      chips.splice(index, 1);
      return [...chips];
    });
    this.filtersUpdated = true;
  }

  npnSelected(event: MatAutocompleteSelectedEvent): void {
    if (!this.chips().includes(event.option.value)) {
      this.chips.update(chips => [...chips, event.option.value]);
    }
    this.currentChip.set('');
    event.option.deselect();
    this.filtersUpdated = true;
  }

  chipDisplay(val) {
    const dispChip = this.chipOptions().find(opt => opt.NPN === val);
    if (dispChip) {
      return `${dispChip.Name} - ${dispChip.NPN}`;
    }

    return val;
  }

  displayUser(val) {
    return val ? `${val.name}` : '';
  }

  changeUserStandalone(ev) {
    this.filtersUpdated = true;
    this.selectedStandaloneUser = this.userControlStandalone.value.id;
  }

  changeSelection(ev) {
    this.fetchingNpns = true;
    this.dtccSvc.getNPNs(ev.option.value.id).subscribe(resp => {
      this.fetchingNpns = false;
      this.chipOptions.set(resp.npns);
    });
  }

  triggerSearch(fromDrilldown = false) {
    // Ignore trigger attempts while a search is already in flight.
    if (this.searching()) {
      return;
    }

    const storedFilters = this.ss.get('BookManagementWidget');
    this.ss.remove('BookManagementWidget');

    // Reverse-map any stored default filters onto the form controls so the UI
    // reflects the search (e.g. when linked in from a widget like Top Performers).
    if (storedFilters && !fromDrilldown) {
      this.hydrateFilters(storedFilters);
    }

    // Build the payload from the form controls so the UI and payload stay in sync.
    const bmWidgetFilters: Record<string, unknown> = {
      unitId: this.selectedUnit,
      userId: [this.selectedStandaloneUser],
      npn: this.chips(),
      start: this.dateRange.start,
      end: this.dateRange.end,
    };

    this.moreFilters.map(bmFilter => {
      if (bmFilter.value() && bmFilter.value().length > 0) {
        bmWidgetFilters[bmFilter.fieldVar] = bmFilter.value();
      }
    });

    this.bmSvc.updateBmWidgetFilters(bmWidgetFilters);

    this.filtersUpdated = false;
  }

  // Reverse of the payload built in triggerSearch: takes a stored filter object
  // and populates the form controls/signals so linked-in defaults render.
  private hydrateFilters(filters) {
    if (!filters) {
      return;
    }

    // NPN chips
    if (filters.npn) {
      this.chips.set(
        Array.isArray(filters.npn) ? [...filters.npn] : [filters.npn]
      );
    }

    // Unit selection (home office / carrier views)
    if (filters.unitId) {
      this.selectedUnit = filters.unitId;
      this.unitCtrl.setValue(filters.unitId, { emitEvent: false });
    }

    // Standalone user selection
    const userId = Array.isArray(filters.userId)
      ? filters.userId[0]
      : filters.userId;
    if (userId) {
      this.selectedStandaloneUser = userId;
    }

    // Issue date range (stored as ISO strings); restore the preset if the range
    // matches a known floating range, otherwise fall back to a custom range.
    if (filters.start || filters.end) {
      this.dateRange = {
        start: filters.start || '',
        end: filters.end || '',
      };
      this.presetRangeControl.setValue(this.matchPresetRange(filters.start), {
        emitEvent: false,
      });
    }

    // More filters (account type, annuity type, carrier, product)
    this.moreFilters.forEach(bmFilter => {
      const value = filters[bmFilter.fieldVar];
      if (value && value.length > 0) {
        bmFilter.value.set(Array.isArray(value) ? [...value] : [value]);
      } else if (value?.selected?.length) {
        bmFilter.value.set(
          Array.isArray(value.selected) ? [...value.selected] : [value.selected]
        );
      } else if (value?.selected === null) {
        bmFilter.value.set([]);
      }
    });

    this.bmSvc.updateBmChipsTrigger(true);

    this.changeDetector.detectChanges();
  }

  // Reverse of the floating-range calculations: finds the preset whose computed
  // start matches the stored start (same day), else 'custom'.
  private matchPresetRange(start): string {
    if (!start) {
      return '';
    }
    const storedStart = DateTime.fromISO(start);
    const match = this.floatingRangeOptions.find(opt => {
      if (opt.value === 'custom') {
        return false;
      }
      const presetStart = this.presetStartDate(opt.value);
      return presetStart
        ? DateTime.fromISO(presetStart).hasSame(storedStart, 'day')
        : false;
    });

    return match ? match.value : 'custom';
  }

  // Recomputes the start date a given preset would produce as of now.
  private presetStartDate(value): string {
    if (value === 'mtd') {
      return DateTime.now().startOf('month').toUTC().toISO();
    }
    if (value === 'ytd') {
      return DateTime.now().startOf('year').toUTC().toISO();
    }
    if (value === '12mtd') {
      return DateTime.now()
        .minus({ years: 1 })
        .startOf('month')
        .toUTC()
        .toISO();
    }

    const [amount, unit] = value.split(' ');
    const timeDiff = {};
    timeDiff[unit] = amount;
    return DateTime.now().minus(timeDiff).startOf('day').toUTC().toISO();
  }

  moreFilterCount() {
    let moreFilterCount = 0;

    this.moreFilters.map(bmFilter => {
      if (bmFilter.value() && bmFilter.value().length > 0) {
        moreFilterCount++;
      }
    });

    return moreFilterCount;
  }

  resetFilters() {
    this.bmSvc.clearBmWidgetFilters(null);

    if (this.defaultRange) {
      this.presetRangeControl.setValue(this.defaultRange);

      this.calculateDateRange(this.defaultRange);
    } else {
      this.clearDateField();
    }

    this.clearRepSearch();
    this.chips.update(() => []);
    this.currentChip.set('');
    this.clearUserStandalone();
    if (
      !(
        this.viewMode === 'rep' &&
        this.homeOfficeUser &&
        !this.ss.get('BookManagementWidget')
      ) ||
      this.isProducer
    )
      this.triggerSearch();
  }

  toggleFiltersUpdated(value) {
    this.filtersUpdated = value;
  }
}
